import type { AxiosInstance } from 'axios';

export type NotificationChannel = 'EMAIL' | 'SMS' | 'WHATSAPP' | 'IN_APP';
export type NotificationStatus = 'QUEUED' | 'SENT' | 'DELIVERED' | 'FAILED';

export interface NotificationTemplate {
  id: string;
  key: string;
  channel: NotificationChannel;
  locale: string;
  subject: string | null;
  body: string;
  isActive: boolean;
  updatedAt: string;
}

export interface NotificationLog {
  id: string;
  templateKey: string;
  channel: NotificationChannel;
  toAddress: string;
  status: NotificationStatus;
  providerId: string | null;
  error: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface UpsertTemplateRequest {
  key: string;
  channel: NotificationChannel;
  locale?: string;
  subject?: string;
  body: string;
  isActive?: boolean;
}

export interface SendNotificationRequest {
  templateKey: string;
  channel: NotificationChannel;
  locale?: string;
  to: string;
  vars?: Record<string, string | number | boolean | null>;
}

/** What a notification is about — drives the icon and where a tap goes. */
export type InAppNotificationType =
  | 'ORDER'
  | 'PAYMENT'
  | 'SHIPPING'
  | 'REFUND'
  | 'WALLET'
  | 'SUPPORT'
  | 'PROMOTION'
  | 'ACCOUNT';

export interface InAppNotification {
  id: string;
  type: InAppNotificationType;
  title: string;
  body: string;
  /** The thing this is about, e.g. `OrderGroup`, so a tap can open it. */
  refType: string | null;
  refId: string | null;
  imageUrl: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface InboxResponse {
  data: InAppNotification[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
  /** The true unread total, not the unread count within this page. */
  unreadCount: number;
}

export interface AlertsSummary {
  total: number;
  pendingVendors: number;
  pendingProducts: number;
  pendingOrders: number;
  pendingReturns: number;
  openTickets: number;
  fetchedAt: string;
}

/** Shape returned by `GET /admin/notifications/dashboard`. */
export interface NotificationDashboard {
  window: { hours: number; since: string };
  delivery: Array<{
    channel: 'EMAIL' | 'SMS' | 'WHATSAPP' | 'IN_APP';
    total: number;
    sent: number;
    queued: number;
    failed: number;
    /** Null when nothing has settled yet in the window. */
    deliveryRate: number | null;
  }>;
  byTemplate: Array<{
    templateKey: string;
    sent: number;
    failed: number;
    queued: number;
    total: number;
  }>;
  recentFailures: Array<{
    id: string;
    channel: string;
    templateKey: string;
    toAddress: string;
    error: string | null;
    createdAt: string;
  }>;
  otp: Array<{
    purpose: string;
    label: string;
    issued: number;
    verified: number;
    burned: number;
    expired: number;
    pending: number;
    verifyRate: number | null;
    policy: { ttlMinutes: number; maxPerHour: number; maxAttempts: number };
  }>;
  providers: {
    sms: {
      provider: string;
      configured: boolean;
      senderId: string | null;
      endpointIsHttps: boolean;
      dltTemplates: Array<{ purpose: string; label: string; configured: boolean }>;
      lastSuccessAt: string | null;
      lastFailureAt: string | null;
      lastError: string | null;
      sentInWindow: number;
      failedInWindow: number;
    };
    email: {
      provider: string;
      configured: boolean;
      host: string | null;
      senderEmail: string | null;
      lastSuccessAt: string | null;
      lastFailureAt: string | null;
      lastError: string | null;
      sentInWindow: number;
      failedInWindow: number;
    };
    storage: { configured: boolean };
  };
  queues: {
    available: boolean;
    reason?: string;
    queues: Array<{
      name: string;
      waiting: number;
      active: number;
      completed: number;
      failed: number;
      retrying: number;
      /** Age of the oldest job still waiting. Null when nothing is queued. */
      oldestWaitingMs: number | null;
      /** Of the sampled head, how many have waited past the stale threshold. */
      staleWaiting: number;
      /**
       * How many jobs were examined. Equal to `staleWaiting` only when every
       * sampled job was stale; qualify the count as "N+" solely when this is
       * below `waiting`, meaning jobs exist beyond the sample.
       */
      staleSampled: number;
      /**
       * Since the last job finished. **Null does not mean workers are dead** —
       * completed jobs are pruned after 24h or 1,000 jobs, whichever comes
       * first, so a quiet night leaves nothing to read. Render it as "no recent
       * activity", never as "stopped".
       */
      lastCompletedMs: number | null;
    }>;
  };
  /**
   * How long messages wait to be picked up, and how often delivery is retried.
   *
   * Percentiles rather than an average: one message stuck for forty minutes
   * barely moves the mean of a thousand fast ones, so an average reads healthy
   * while a tail arrives hours late. Null when nothing has been delivered in the
   * window — no samples, not zero delay.
   */
  pipeline: {
    sampleSize: number;
    queueDelayMs: { p50: number | null; p95: number | null; max: number | null };
    retry: {
      messagesRetried: number;
      totalRetries: number;
      retryRate: number | null;
      averageRetries: number;
    };
    byChannel: Array<{
      channel: string;
      attempted: number;
      retried: number;
      retryRate: number | null;
    }>;
  };
  generatedAt: string;
}

export function notificationsEndpoints(http: AxiosInstance) {
  return {
    /** Operational delivery dashboard. */
    dashboard: (windowHours = 24) =>
      http
        .get<NotificationDashboard>('/admin/notifications/dashboard', { params: { windowHours } })
        .then((r) => r.data),

    listTemplates: () =>
      http
        .get<{ data: NotificationTemplate[] }>('/admin/notifications/templates')
        .then((r) => r.data.data),
    upsertTemplate: (body: UpsertTemplateRequest) =>
      http.put<NotificationTemplate>('/admin/notifications/templates', body).then((r) => r.data),
    send: (body: SendNotificationRequest) =>
      http.post('/admin/notifications/send', body).then((r) => r.data),
    listLogs: (params?: { templateKey?: string; status?: NotificationStatus; limit?: number }) =>
      http
        .get<{ data: NotificationLog[] }>('/admin/notifications/logs', { params })
        .then((r) => r.data.data),

    /** Aggregated pending-action counts — powers the admin shell bell. */
    // ── the signed-in customer's inbox ──────────────────────────────────
    //
    // Distinct from everything else in this module, which is the admin's view
    // of what was *sent*. These are the messages themselves, addressed to the
    // caller, and none of them takes a user id — the token decides whose
    // inbox this is.

    myList: (params?: { page?: number; pageSize?: number; unreadOnly?: boolean }) =>
      http.get<InboxResponse>('/me/notifications', { params }).then((r) => r.data),

    /** Just the badge count, cheap enough to poll on screen focus. */
    myUnreadCount: () =>
      http
        .get<{ unreadCount: number }>('/me/notifications/unread-count')
        .then((r) => r.data.unreadCount),

    markRead: (notificationId: string) =>
      http
        .post<{ updated: number }>(`/me/notifications/${notificationId}/read`)
        .then((r) => r.data),

    markAllRead: () =>
      http.post<{ updated: number }>('/me/notifications/read-all').then((r) => r.data),

    alertsSummary: () => http.get<AlertsSummary>('/admin/alerts/summary').then((r) => r.data),
  };
}

export type NotificationsEndpoints = ReturnType<typeof notificationsEndpoints>;
